import _ from 'lodash';

import {
  NUM_DISTINCT_COLORS,
  isAnnotationValid,
  getAnnotationId,
  getDisplayLabel,
  BreakdownColors,
  BreakdownColorsDict,
  BreakdownColorsArray,
} from '@scale/shared/annotation';
import { getConverterForGeometry } from '@scale/shared/geometryConverters';
import { Point2D } from '@scale/shared/types/math';
import {
  AnnotationGeometry,
  BoxProps,
  Annotation,
  AnnotationPathCurrent,
  AnnotationAttribute,
  AnnotationFrame,
  ApiAnnotation,
} from '@scale/shared/types/annotation';
import { AnnotationAttributeType } from '@scale/shared/defaults';
import { IGeometryParams, LabelChoice } from '@scale/shared/types/params/annotation';
import random from '@scale/frontend-lib/random';
import { NESTED_ANNOTATION_ATTRIBUTE_PATH_DELIMITER } from 'react-scale/components/scalers/common/AnnotationAttributeInput/CategoryFieldSectionWrapper';
import { ILink } from '@scale/shared/annotation/link/ILink';

/* -----------------------------------------------------------------------------
 * 2D path annotation - rendering
 * ----------------------------------------------------------------------------- */

// wanted to bring this method into shared without having to refactor all the imports that call it
export { getAnnotationId };

export const calculateImageSpaceCoordinates = (
  cursorPos: Point2D | null,
  svgCanvas: SVGSVGElement | null,
): Point2D => {
  if (!svgCanvas || !svgCanvas.getScreenCTM() || !cursorPos) {
    return { x: 0, y: 0 };
  }
  const { x, y } = cursorPos;
  const svgPoint = svgCanvas.createSVGPoint();
  svgPoint.x = x;
  svgPoint.y = y;
  const domPoint = svgPoint.matrixTransform(svgCanvas.getScreenCTM()!.inverse());
  return { x: domPoint.x, y: domPoint.y };
};

export const calculateCanvasCoordinates = (
  imagePos: Point2D | null,
  svgCanvas: SVGSVGElement | null,
): Point2D => {
  if (!svgCanvas || !svgCanvas.getScreenCTM() || !imagePos) return { x: 0, y: 0 };
  const svgPoint = svgCanvas.createSVGPoint();
  svgPoint.x = imagePos.x;
  svgPoint.y = imagePos.y;
  const canPoint = svgPoint.matrixTransform(svgCanvas.getScreenCTM() as DOMMatrixInit);
  return { x: canPoint.x, y: canPoint.y };
};

// Returns a string - not JSX - for compatibility within SVG annotations
export function getAnnotationText({
  annotation,
  selectedBreakdown,
  labelChoices = [],
  defaultLabel = '',
  defaultValue = '\u2026',
  choiceDisplayMap = {},
  options = {},
  delimitedAttributeLabels = false,
}: {
  annotation: $FIXME;
  selectedBreakdown: string | null;
  labelChoices?: LabelChoice[];
  defaultLabel?: string;
  defaultValue?: string;
  choiceDisplayMap?: Record<string, string>;
  options?: {
    lidarLinkingDistances?: $FIXME;
  };
  delimitedAttributeLabels?: boolean;
}) {
  const label = annotation.label ? annotation.label : defaultLabel;

  if (selectedBreakdown === 'label') {
    return getDisplayLabel(label, labelChoices, choiceDisplayMap);
  }

  if (selectedBreakdown === 'accuracy' && _.isNumber(annotation.accuracy)) {
    const accString = ((annotation.accuracy || 0) * 100).toFixed(2) + '%';
    return accString;
  }

  if (selectedBreakdown === 'id' || selectedBreakdown === 'accuracy') {
    const annotationId = getAnnotationId(annotation) || '';
    const endOfId = annotationId.substring(annotationId.length - 4);

    return `${getDisplayLabel(label, labelChoices, choiceDisplayMap)} ${endOfId}`;
  }

  if (selectedBreakdown === 'lidarLinkingDistance') {
    const distance: number | undefined = _.get(options, ['lidarLinkingDistances', annotation.uuid]);
    if (distance && _.isFinite(distance)) {
      return (+distance.toFixed(1)).toString();
    }
    return '...';
  }

  if (selectedBreakdown) {
    if (annotation.attributes && annotation.attributes[selectedBreakdown]) {
      return delimitedAttributeLabels
        ? annotation.attributes[selectedBreakdown]
            .toString()
            .split(NESTED_ANNOTATION_ATTRIBUTE_PATH_DELIMITER)
            .pop()
        : annotation.attributes[selectedBreakdown].toString();
    } else {
      return defaultValue;
    }
  }

  return null;
}

export function getLabelColor(
  label: string,
  breakdownColorsDict: BreakdownColorsDict,
  options?: {
    defaultColor?: string;
  },
) {
  return breakdownColorsDict[label] ?? options?.defaultColor ?? '';
}

export const VIDEO_DEFAULT_COLOR = '#666';

function getAnnotationPathColor(
  annotationPath: AnnotationPathCurrent<Annotation>,
  selectedBreakdown: string | null,
  breakdownColors: $FIXME,
) {
  if (!annotationPath) return null;

  const labelText = getAnnotationPathText(annotationPath, selectedBreakdown ?? '', undefined);

  // Appearance of annotationPath box
  let bgColor;
  if (selectedBreakdown === 'id') {
    bgColor =
      breakdownColors[annotationPath.index % Math.min(NUM_DISTINCT_COLORS, breakdownColors.length)];
  } else {
    bgColor = breakdownColors[labelText];
  }

  return bgColor || VIDEO_DEFAULT_COLOR;
}

// eslint-disable-next-line complexity
export function getAnnotationColor(
  annotation: (Annotation & { [key: string]: any }) | $FIXME | null, // to get rid of fixme, need to make attributes optional and then fix resulting type errors
  selectedBreakdown: string | null,
  breakdownColors: BreakdownColors,
  options?: {
    attributeDefinitions?: {
      [key: string]: AnnotationAttribute;
    };
    defaultColor?: string;
    frame?: number;
    annotationPath?: AnnotationPathCurrent<Annotation>;
    linksFrom?: ILink[];
    flagged?: boolean;
  },
): string {
  const breakdownColorsDict = breakdownColors as BreakdownColorsDict;
  const breakdownColorsArray = breakdownColors as BreakdownColorsArray;
  const defaultColor = options?.defaultColor ?? '';
  const attributeDefinitions = options?.attributeDefinitions;
  const annotationPath = options?.annotationPath;

  if (!annotation || options?.flagged) {
    return defaultColor;
  }

  if (!annotation && !!annotationPath) {
    return getAnnotationPathColor(annotationPath, selectedBreakdown, breakdownColors);
  }

  if (selectedBreakdown === 'accuracy') {
    return breakdownColorsDict[getAnnotationId(annotation)] || defaultColor;
  }

  if (selectedBreakdown === 'label') {
    return getLabelColor(annotation.label!, breakdownColorsDict, options);
  }

  if (selectedBreakdown === 'id') {
    const id = parseInt(getAnnotationId(annotation).replace('-', ''), 16);
    return breakdownColorsArray[id % Math.min(NUM_DISTINCT_COLORS, breakdownColorsArray.length)];
  }

  // XXX: Assumes that breakdownColors is a function, not an object
  if (
    typeof breakdownColors === 'function' &&
    selectedBreakdown === 'lidarLinkingDistance' &&
    options &&
    options.frame !== undefined
  ) {
    // need uuid to be defined for this to work
    return breakdownColors({ ...annotation, uuid: annotation.uuid! }, options.frame);
  }

  if (annotation.attributes && selectedBreakdown) {
    const attrType = attributeDefinitions?.[selectedBreakdown]?.type;
    if (attrType === AnnotationAttributeType.Category || _.isNil(attrType)) {
      const colorFromAnn = breakdownColorsDict[annotation.attributes[selectedBreakdown] as string];

      // Imperfect color by link attribute; Only colors by the attr value of the first link that is using the attr
      if (!colorFromAnn) {
        const firstLinkUsingAttribute = options?.linksFrom?.find(
          link => !_.isNil(link.attributes[selectedBreakdown]),
        );

        if (firstLinkUsingAttribute) {
          return breakdownColorsDict[
            firstLinkUsingAttribute.attributes[selectedBreakdown] as string
          ];
        } else {
          return defaultColor;
        }
      }
      return colorFromAnn;
    } else {
      const hashCode = Math.abs(
        random.stringHash(_.toString(annotation.attributes[selectedBreakdown])),
      );
      return breakdownColorsArray[hashCode % breakdownColorsArray.length] || defaultColor;
    }
  }

  return defaultColor;
}

/* -----------------------------------------------------------------------------
 * Annotation conversion from backend <==> frontend formats
 * These functions are analogous to those in videoUtils.ts
 * TODO: unify single-frame and video conversions
 * ----------------------------------------------------------------------------- */

export const findAndConvertOriginalAnnotation = (
  uuid: string,
  geometry: AnnotationGeometry,
  frame: number,
  originalAnnotations: AnnotationFrame<ApiAnnotation>[] | null,
): Annotation | undefined => {
  const originalAnnotation =
    originalAnnotations && _.find(originalAnnotations?.[frame]?.annotations, b => b.uuid === uuid);
  if (originalAnnotation) {
    return convertOriginalAnnotation(originalAnnotation, geometry);
  }
};

export const findAndConvertOriginalAnnotationImage = (
  uuid: string,
  geometry: AnnotationGeometry,
  originalAnnotations: Annotation[] | null,
): Annotation | undefined => {
  const originalAnnotation =
    originalAnnotations && _.find(originalAnnotations, b => b.uuid === uuid);
  if (originalAnnotation) {
    return convertOriginalAnnotation(originalAnnotation, geometry);
  }
};

const convertOriginalAnnotation = (originalAnnotation: $FIXME, geometry: AnnotationGeometry) => {
  switch (geometry) {
    case AnnotationGeometry.Box:
      return {
        attributes: originalAnnotation.attributes,
        x: originalAnnotation.left,
        y: originalAnnotation.top,
        width: originalAnnotation.width,
        height: originalAnnotation.height,
      };
    case AnnotationGeometry.Cuboid:
      return getConverterForGeometry(AnnotationGeometry.Cuboid).toLabelerFormat({
        type: AnnotationGeometry.Cuboid,
        attributes: originalAnnotation.attributes,
        vertices: originalAnnotation.vertices,
        edges: originalAnnotation.edges,
        uuid: originalAnnotation.uuid,
        label: originalAnnotation.label,
      });
    case AnnotationGeometry.Polygon:
      return {
        attributes: originalAnnotation.attributes,
        vertices: originalAnnotation.vertices,
      };
    default:
      return { attributes: originalAnnotation.attributes };
  }
};

/* -----------------------------------------------------------------------------
 * Single-frame annotation
 * ----------------------------------------------------------------------------- */

export interface ValidBoxOpts {
  minimumWidth?: number;
  minimumHeight?: number;
}

export function isBoxValid(b: BoxProps, opts: ValidBoxOpts) {
  if (!b) {
    return false;
  }

  const { width, height } = b;
  if (!width || !height) {
    return false;
  }

  const minWidth = opts.minimumWidth || 2;
  const minHeight = opts.minimumHeight || 2;
  const isWideEnough = minWidth <= width;
  const isTallEnough = minHeight <= height;

  return isWideEnough && isTallEnough;
}

/*
 * Returns false if the annotation has any WARNING level problems in any frame,
 * i.e. one or more of:
 *   - WARNING level problem with its geometry
 */
export function isAnnotationPathValid(
  annotationPath: AnnotationPathCurrent<Annotation>,
  taskType: string,
  customShapes: IGeometryParams = {},
) {
  return _.every(annotationPath.frames, annotation => {
    return (
      !annotation ||
      isAnnotationValid({
        annotation,
        taskType,
        customShapes,
        label: annotationPath.label,
      })
    );
  });
}

/* -----------------------------------------------------------------------------
 * Video box and cuboid annotation - rendering
 * ----------------------------------------------------------------------------- */

export function getAnnotationPathText(
  annotationPath: AnnotationPathCurrent<Annotation>,
  selectedBreakdown: string,
  frameIndex?: number,
  defaultValue = '\u2026',
): string {
  const endOfId = getAnnotationId(annotationPath).substring(
    getAnnotationId(annotationPath).length - 4,
  );

  const geometry = annotationPath.geometry;
  const label = annotationPath.label || geometry;
  let labelText;

  // Determine text (label or attribute value) for annotation label
  if (selectedBreakdown === 'id') {
    labelText = `${label} ${endOfId}`;
  } else if (selectedBreakdown === 'label') {
    labelText = label;
  } else if (selectedBreakdown) {
    const frameAttributes =
      frameIndex !== undefined && frameIndex !== null
        ? annotationPath.attributes[frameIndex]
        : annotationPath.currentFrameAnnotation?.attributes;
    labelText = frameAttributes?.[selectedBreakdown];
  }

  labelText = labelText ?? defaultValue;

  return '' + labelText;
}
